import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  CircularProgress,
  IconButton,
  Backdrop,
  Fade
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface TaskCreationProcessingModalProps {
  open: boolean;
  onClose: () => void;
}

const TaskCreationProcessingModal: React.FC<TaskCreationProcessingModalProps> = ({
  open,
  onClose
}) => {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [showPhrase, setShowPhrase] = useState(true);

  // Creative and humorous phrases that cycle every 3 seconds
  const loadingPhrases = [
    "🎯 Generating tasks with military precision...",
    "✨ Crafting detailed prompts like a literary genius...",
    "🧠 Teaching your AI teammates their new assignments...",
    "🎪 Orchestrating a symphony of productivity...",
    "🔮 Weaving task magic with algorithmic sorcery...",
    "🚀 Launching tasks into the productivity stratosphere..."
  ];

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setShowPhrase(false);

      // After fade out, change phrase and fade back in
      setTimeout(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % loadingPhrases.length);
        setShowPhrase(true);
      }, 300); // Wait for fade out to complete
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [open, loadingPhrases.length]);

  // Reset to first phrase when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPhraseIndex(0);
      setShowPhrase(true);
    }
  }, [open]);

  return (
    <>
      {/* Backdrop that closes modal on click */}
      <Backdrop
        open={open}
        onClick={onClose}
        style={{
          zIndex: 1201,
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
      />

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 8,
            minHeight: 200,
            position: 'relative'
          }
        }}
        // Prevent default backdrop to use custom one
        BackdropComponent={() => null}
        style={{
          zIndex: 1202
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#9e9e9e'
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 150,
              gap: 24,
              paddingTop: 16,
              paddingBottom: 16,
            }}
          >
            <CircularProgress size={50} />

            <div style={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Creating Tasks
              </Typography>

              {/* Cycling humorous phrases */}
              <Fade in={showPhrase} timeout={300}>
                <Typography
                  variant="body1"
                  color="primary"
                  paragraph
                  style={{
                    minHeight: '1.5em',
                    fontWeight: 500,
                    fontSize: '1.1rem'
                  }}
                >
                  {loadingPhrases[currentPhraseIndex]}
                </Typography>
              </Fade>

              <Typography variant="body2" color="text.secondary" paragraph>
                Task creation may take a few minutes as we craft detailed assignments for your AI teammates.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click anywhere to close this message
              </Typography>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaskCreationProcessingModal;